import { useState, useRef } from 'react';
import * as XLSX from 'xlsx';
import api from '../api';
import { DownloadIcon, UsersIcon, BuildingIcon, CheckIcon, AlertIcon, LayersIcon } from '../components/Icons';

const EMP_HEADERS = ['name','project','gender','designation','joiningDate','dob','iqamaNo','iqamaExpiry','iqamaStatus','qiwaStart','qiwaEnd','passportNo','passportExpiry','healthInsuranceExpiry','nationality','basicSalary','allowance','totalSalary','contact','sponsorship','iban','deductionBalance','status','comments'];
const EMP_SAMPLE = { name: 'Mohammed Ali', project: 'Movenpick', gender: 'Male', designation: 'General Cleaner', joiningDate: '2025-03-01', dob: '1995-06-15', iqamaNo: '2501234567', iqamaExpiry: '2026-12-31', iqamaStatus: 'Renewed', qiwaStart: '2025-03-01', qiwaEnd: '2026-03-01', passportNo: 'AB1234567', passportExpiry: '2028-01-01', healthInsuranceExpiry: '2026-09-30', nationality: 'Bangladeshi', basicSalary: 1200, allowance: 300, totalSalary: 1500, contact: '0501234567', sponsorship: 'Delta Al Mumayyaza', iban: 'SA0000000000000000000000', deductionBalance: 0, status: 'Active', comments: '' };

const CLI_HEADERS = ['name','contactPerson','contactNo','email','location','crNumber','vatNumber','otMultiplier','status','notes'];
const CLI_SAMPLE = { name: 'Movenpick', contactPerson: 'Ahmed Khan', contactNo: '0501111111', email: 'hr@movenpick.com', location: 'Jeddah', crNumber: '4030603769', vatNumber: '300000000000003', otMultiplier: 1.5, status: 'Active', notes: '' };

function downloadTemplate(kind) {
  const headers = kind === 'employees' ? EMP_HEADERS : CLI_HEADERS;
  const sample = kind === 'employees' ? EMP_SAMPLE : CLI_SAMPLE;
  const ws = XLSX.utils.json_to_sheet([sample], { header: headers });
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, kind);
  XLSX.writeFile(wb, `delta-hr-${kind}-template.xlsx`);
}

// Excel serial date → YYYY-MM-DD
function normDate(v) {
  if (v === undefined || v === null || v === '') return '';
  if (typeof v === 'number') {
    const d = new Date(Math.round((v - 25569) * 86400 * 1000));
    return isNaN(d) ? '' : d.toISOString().slice(0, 10);
  }
  const d = new Date(v);
  return isNaN(d) ? String(v) : d.toISOString().slice(0, 10);
}

const DATE_FIELDS = ['joiningDate','dob','iqamaExpiry','qiwaStart','qiwaEnd','passportExpiry','healthInsuranceExpiry'];

function ImportCard({ kind, icon: Icon, title, desc, headers }) {
  const [state, setState] = useState('idle'); // idle | parsing | uploading | done | error
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');
  const fileRef = useRef();

  async function handleFile(e) {
    const file = e.target.files[0];
    e.target.value = '';
    if (!file) return;
    setState('parsing'); setError(''); setResult(null);
    try {
      const buf = await file.arrayBuffer();
      const wb = XLSX.read(buf);
      const ws = wb.Sheets[wb.SheetNames[0]];
      let rows = XLSX.utils.sheet_to_json(ws, { defval: '' });
      if (!rows.length) throw new Error('No data rows found in the sheet');
      if (kind === 'employees') {
        rows = rows.map(r => {
          const out = { ...r };
          DATE_FIELDS.forEach(f => { if (out[f] !== undefined) out[f] = normDate(out[f]); });
          return out;
        });
      }
      setState('uploading');
      const resp = await api.post(`/${kind}/bulk`, { rows });
      setResult(resp.data);
      setState('done');
    } catch (err) {
      setError(err.response?.data?.error || err.message || 'Import failed');
      setState('error');
    }
  }

  return (
    <div className="bg-white rounded-2xl border border-slate-100 p-6" style={{ boxShadow: '0 1px 3px rgba(15,27,51,0.06)' }}>
      <div className="flex items-center gap-3 mb-3">
        <div className="w-10 h-10 rounded-xl bg-[#0f1b33] flex items-center justify-center">
          <Icon size={18} className="text-white" />
        </div>
        <div>
          <p className="text-[15px] font-bold text-slate-800">{title}</p>
          <p className="text-xs text-slate-400">{desc}</p>
        </div>
      </div>

      <div className="text-[11.5px] text-slate-400 bg-slate-50 rounded-xl p-3 mb-4 font-mono leading-relaxed break-all">
        {headers.join(' · ')}
      </div>

      <div className="flex gap-2.5 flex-wrap">
        <button onClick={() => downloadTemplate(kind)}
          className="flex items-center gap-2 px-4 py-2.5 rounded-full text-[13px] font-semibold border border-slate-200 text-slate-600 hover:border-[#fb866d] hover:text-[#e8614a] transition-all">
          <DownloadIcon size={14} /> Download Template
        </button>
        <button onClick={() => fileRef.current.click()} disabled={state === 'parsing' || state === 'uploading'}
          className="flex items-center gap-2 px-5 py-2.5 rounded-full text-white text-[13px] font-semibold bg-[#fb866d] hover:bg-[#e8614a] transition-all disabled:opacity-50">
          {state === 'parsing' ? 'Reading file...' : state === 'uploading' ? 'Importing...' : 'Upload Excel'}
        </button>
        <input ref={fileRef} type="file" accept=".xlsx,.xls,.csv" className="hidden" onChange={handleFile} />
      </div>

      {state === 'done' && result && (
        <div className="mt-4 px-4 py-3 rounded-xl bg-emerald-50 border border-emerald-200 text-[13px] text-emerald-800">
          <div className="flex items-center gap-2 font-semibold"><CheckIcon size={14} /> Import complete</div>
          <div className="mt-1">{result.created} created · {result.updated} updated{result.skipped > 0 ? ` · ${result.skipped} skipped` : ''}</div>
          {result.errors?.length > 0 && (
            <ul className="mt-2 text-[12px] text-amber-700 list-disc pl-4">
              {result.errors.map((e, i) => <li key={i}>{e}</li>)}
            </ul>
          )}
        </div>
      )}
      {state === 'error' && (
        <div className="mt-4 flex items-start gap-2 px-4 py-3 rounded-xl bg-red-50 border border-red-200 text-[13px] text-red-600">
          <AlertIcon size={14} className="mt-0.5 flex-shrink-0" /> {error}
        </div>
      )}
    </div>
  );
}

export default function DataImport() {
  return (
    <div>
      <div className="hidden lg:flex bg-white border-b border-slate-100 px-8 py-4 items-center justify-between sticky top-0 z-40">
        <h1 className="text-lg font-bold text-slate-800">Data Import</h1>
        <span className="px-3 py-1.5 bg-slate-50 border border-slate-200 rounded-full text-xs font-semibold text-slate-600">Admin</span>
      </div>

      <div className="p-4 lg:p-8 stagger">
        <div className="flex items-center gap-4 mb-6">
          <div className="w-12 h-12 rounded-xl bg-[#0f1b33] flex items-center justify-center">
            <LayersIcon size={22} className="text-white" />
          </div>
          <div>
            <h2 className="text-[17px] font-bold text-slate-800">Bulk Import from Excel</h2>
            <p className="text-[13px] text-slate-400">Download a template, fill it, upload. Existing records are matched and updated — employees by Iqama No., clients by name.</p>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
          <ImportCard kind="employees" icon={UsersIcon} title="Employees"
            desc="Add or update employees in bulk. Dates: YYYY-MM-DD."
            headers={EMP_HEADERS} />
          <ImportCard kind="clients" icon={BuildingIcon} title="Clients / Projects"
            desc="Add or update client sites in bulk."
            headers={CLI_HEADERS} />
        </div>

        <p className="text-xs text-slate-400 mt-4">
          Rows missing required fields (employee: name + project · client: name) are skipped and reported. Import never deletes records.
        </p>
      </div>
    </div>
  );
}